"use client";
import { createContext, useState, ReactNode, useEffect, useContext, useMemo } from "react";
import { FormStepType } from "@/data/forms/types";
import { getFormSchemaBySlug, getPdfIdBySlug } from "@/utils/Form/formUtils";
import useGetFormDetails from "@/hooks/useGetFormDetails";
import * as log from "@/actions/logger";

interface SchemaContextProps {
  schemaSlug: string | null;
  setSchemaSlug: (formSlug: string) => void;
  formSchema: FormStepType[] | null;
  setFormSchema: (schema: FormStepType[] | null) => void;
  pdfId: string | null;
  setPdfId: (pdfId: string) => void;
  formDetails: Record<string, any> | undefined;
}

export const SchemaContext = createContext<SchemaContextProps | undefined>(undefined);

export const SchemaProvider = ({ children }: { children: ReactNode }) => {
  const { getFormDetails } = useGetFormDetails();
  const [schemaSlug, setSchemaSlug] = useState<string | null>(null);
  const [formSchema, setFormSchema] = useState<FormStepType[] | null>(null);
  const [pdfId, setPdfId] = useState<string | null>(null);
  const [formDetails, setFormDetails] = useState<Record<string, any>>();

  const value = useMemo(
    () => ({
      schemaSlug,
      setSchemaSlug,
      formSchema,
      setFormSchema,
      pdfId,
      setPdfId,
      formDetails,
    }),
    [schemaSlug, formSchema, pdfId, formDetails]
  );

  useEffect(() => {
    const fetchFormDetails = async (pdfId: string) => {
      try {
        const data = await getFormDetails({ pdfId });
        if (data) setFormDetails(data);
      } catch (error) {
        log.error({ err: error, module: "FormSchemaContext" }, "Error getting formDetails");
      }
    };

    if (schemaSlug) {
      const schema = getFormSchemaBySlug(schemaSlug);
      setFormSchema(schema);
      const pdfId = getPdfIdBySlug(schemaSlug);
      setPdfId(pdfId);

      if (pdfId) {
        fetchFormDetails(pdfId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemaSlug]);

  return <SchemaContext.Provider value={value}>{children}</SchemaContext.Provider>;
};

export const useSchema = () => {
  const context = useContext(SchemaContext);
  if (!context) throw new Error("useSchema must be used inside SchemaProvider");
  return context;
};
